import { useEffect, useState } from "react";
import { getPricingPolicy, savePricingPolicy, type PricingPolicy } from "./actions";

// Pricing: the rule that turns a vendor cost into the console's selling price (the one number used on the page, in the Merchant Center feed and in
// the structured data). Cost-plus markup, raised to MAP when below it (MAP is a floor, never a ceiling), rounded up to x.99, and never based on the
// vendor's list price. The shipped numbers are a DRAFT until someone saves the policy here; every save is a new version and the old ones stay.

const FEEDS = [["ndc", "NDC"], ["dnh", "D&H"], ["kroll", "Kroll"]] as const;

type Override = { scope: "feed" | "brand" | "product"; key: string; markup_pct: number };
type Version = { version?: number; default_markup_pct?: number; min_margin_pct?: number; rounding?: string; cap_to_list?: boolean; overrides?: Override[]; updated_by?: string; updated_at?: string; note?: string };

/** What a saved version changed compared with the one before it, in words. The first version is the starting policy. */
export function describeChange(prev: Version | undefined, cur: Version): string[] {
  if (!prev) return ["Starting policy"];
  const out: string[] = [];
  if (prev.default_markup_pct !== cur.default_markup_pct) out.push(`Default markup ${prev.default_markup_pct}% to ${cur.default_markup_pct}%`);
  if (prev.min_margin_pct !== cur.min_margin_pct) out.push(`Minimum margin ${prev.min_margin_pct}% to ${cur.min_margin_pct}%`);
  if (prev.rounding !== cur.rounding) out.push(`Rounding ${prev.rounding} to ${cur.rounding}`);
  if (!!prev.cap_to_list !== !!cur.cap_to_list) out.push(cur.cap_to_list ? "Flag prices above the list price: on" : "Flag prices above the list price: off");
  const id = (o: Override) => `${o.scope}:${o.key.toLowerCase()}`;
  const before = new Map((prev.overrides ?? []).map((o) => [id(o), o])), after = new Map((cur.overrides ?? []).map((o) => [id(o), o]));
  for (const [k, o] of after) {
    const b = before.get(k);
    if (!b) out.push(`Override added: ${o.scope} ${o.key} at ${o.markup_pct}%`);
    else if (b.markup_pct !== o.markup_pct) out.push(`Override ${o.scope} ${o.key}: ${b.markup_pct}% to ${o.markup_pct}%`);
  }
  for (const [k, o] of before) if (!after.has(k)) out.push(`Override removed: ${o.scope} ${o.key}`);
  return out.length ? out : ["No change to the rules"];
}

export function PricingPanel({ view = "policy" }: { view?: "policy" | "versions" }) {
  const [p, setP] = useState<PricingPolicy | null>(null);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<"feed" | "brand" | "product">("feed");
  const [key, setKey] = useState("ndc");
  const [markup, setMarkup] = useState("");

  const load = async () => {
    const r = await getPricingPolicy();
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    setP({ ...r.data.policy, overrides: r.data.policy.overrides ?? [] });
    setHistory(r.data.history);
  };
  useEffect(() => { load(); }, []);

  if (!p) return <div className="panel">{error ?? "Loading…"}</div>;
  const set = (patch: Partial<PricingPolicy>) => { setP({ ...p, ...patch }); setMsg(null); };

  const addOverride = () => {
    const m = Number(markup);
    if (!key.trim() || !Number.isFinite(m) || m < 0) { setError("An override needs a feed or brand and a markup of 0 or more."); return; }
    setError(null);
    set({ overrides: [...p.overrides.filter((o) => !(o.scope === scope && o.key.toLowerCase() === key.trim().toLowerCase())),
                      { scope, key: key.trim(), markup_pct: m }] });
    setMarkup("");
  };

  const save = async () => {
    setSaving(true);
    const r = await savePricingPolicy(p, note.trim());
    setSaving(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    setMsg(`Saved as version ${r.data.version}. The Sheet shows the new prices now; the approval counts rebuild next.`);
    setNote("");
    await load();
  };

  return (
    <div>
      <p className="meta" style={{ marginTop: 0 }}>
        The selling price is cost plus a markup, raised to the vendor's MAP when below it, rounded up to the next .99. A product with no vendor cost gets no selling price
        (the vendor's list price is never used). {p.confirmed ? `Policy version ${p.version}, saved by ${p.updated_by || "?"} on ${String(p.updated_at || "").slice(0, 10)}.`
          : "This is the DRAFT default: nothing here has been decided yet. Save it (even unchanged) to confirm it."}
      </p>
      {error && <div className="panel error">{error}</div>}
      {msg && <div className="panel">{msg}</div>}

      {view === "policy" && <div className="panel" style={{ maxWidth: 760 }}>
        <div style={{ display: "grid", gridTemplateColumns: "auto 120px", gap: "10px 16px", alignItems: "center" }}>
          <label>Default markup on cost (%)</label>
          <input type="number" min={0} step="0.5" value={p.default_markup_pct} onChange={(e) => set({ default_markup_pct: Number(e.target.value) })} />
          <label>Minimum margin on price (%): below this a product is flagged</label>
          <input type="number" min={0} step="0.5" value={p.min_margin_pct} onChange={(e) => set({ min_margin_pct: Number(e.target.value) })} />
          <label>Rounding</label>
          <select value={p.rounding} onChange={(e) => set({ rounding: e.target.value as "x.99" | "none" })}>
            <option value="x.99">Up to x.99</option><option value="none">Cents</option>
          </select>
          <label>Flag a price above the vendor's list price</label>
          <input type="checkbox" checked={p.cap_to_list} onChange={(e) => set({ cap_to_list: e.target.checked })} />
        </div>

        <h3 style={{ margin: "18px 0 6px" }}>Overrides</h3>
        <p className="meta" style={{ marginTop: 0 }}>A product override beats a brand override, which beats a feed override, which beats the default.</p>
        <table className="manual-products">
          <thead><tr><th>Applies to</th><th>Name</th><th>Markup %</th><th></th></tr></thead>
          <tbody>
            {p.overrides.map((o) => (
              <tr key={o.scope + o.key}>
                <td>{o.scope === "feed" ? "Feed" : o.scope === "brand" ? "Brand" : "Product"}</td><td>{o.scope === "feed" ? (FEEDS.find((f) => f[0] === o.key)?.[1] ?? o.key) : o.key}</td>
                <td className="num">{o.markup_pct}</td>
                <td><button type="button" onClick={() => set({ overrides: p.overrides.filter((x) => x !== o) })}>Remove</button></td>
              </tr>
            ))}
            {p.overrides.length === 0 && <tr><td colSpan={4} className="meta">No overrides: every product uses the default.</td></tr>}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <select value={scope} onChange={(e) => { setScope(e.target.value as "feed" | "brand" | "product"); setKey(e.target.value === "feed" ? "ndc" : ""); }}>
            <option value="feed">Feed</option><option value="brand">Brand</option><option value="product">Product</option>
          </select>
          {scope === "feed"
            ? <select value={key} onChange={(e) => setKey(e.target.value)}>{FEEDS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select>
            : <input placeholder={scope === "brand" ? "Brand, as the feed spells it" : "Product id, e.g. dnh-ABC123"} value={key} onChange={(e) => setKey(e.target.value)} style={{ minWidth: 220 }} />}
          <input type="number" min={0} step="0.5" placeholder="Markup %" style={{ width: 110 }} value={markup} onChange={(e) => setMarkup(e.target.value)} />
          <button type="button" onClick={addOverride}>Add override</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder="Note for this version (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
          <button type="button" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save policy"}</button>
        </div>
      </div>}

      {view === "versions" && <>
      <p className="meta">Every save of the policy is a new version, and the earlier ones stay. Each row shows what that save changed from the one before.</p>
      <div className="sheet-scroll" style={{ flex: "none", maxHeight: "none" }}>
      <table className="manual-products">
        <thead><tr><th>Version</th><th>When</th><th>Saved by</th><th>What changed</th><th>Default markup %</th><th>Min margin %</th><th>Rounding</th><th>Overrides</th><th>Note</th></tr></thead>
        <tbody>
          {history.map((h, i) => {
            const v = h as unknown as Version, older = history[i + 1] as unknown as Version | undefined;
            return (
              <tr key={String(h.version)}>
                <td className="num">{String(h.version)}{i === 0 ? " (current)" : ""}</td><td className="mono">{String(h.updated_at ?? "").slice(0, 16).replace("T", " ")}</td><td>{String(h.updated_by ?? "")}</td>
                <td>{describeChange(older, v).map((c) => <div key={c}>{c}</div>)}</td>
                <td className="num">{String(h.default_markup_pct)}</td><td className="num">{String(h.min_margin_pct)}</td><td>{String(h.rounding)}</td>
                <td>{(v.overrides ?? []).map((o) => `${o.scope} ${o.key} ${o.markup_pct}%`).join(", ") || "none"}</td><td>{String(h.note ?? "")}</td>
              </tr>);
          })}
          {history.length === 0 && <tr><td colSpan={9} className="meta">No saved versions yet: the policy is still the draft default. Save it on the Pricing policy tab to start the history.</td></tr>}
        </tbody>
      </table>
      </div>
      </>}
    </div>
  );
}
